import styled from "styled-components";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faTimeline,
  faTriangleExclamation,
  faLungs,
} from "@fortawesome/free-solid-svg-icons";
import { COLOURS } from "../theme";

type Project = {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  tags: string[];
  module: string;
};

const Icon = styled(FontAwesomeIcon)`
  color: ${COLOURS.text};
  font-size: 22px;
`;

const projects: Project[] = [
  {
    id: "breathe-well",
    title: "BreatheWell",
    description:
      "A mobile app designed to support people living with COPD through daily tracking and emotional companionship. It helps users monitor their symptoms and medication, access clear self‑management resources, and connect with others through a community forum. The app also includes an AI companion that offers reflective prompts, aiming to reduce isolation and strengthen confidence in managing chronic symptoms.",
    href: "https://github.com/amylouisejordan/breathe-well",
    icon: <Icon icon={faLungs} aria-label="Lungs icon" />,
    tags: ["React Native", "Firebase", "Expo", "Bedrock"],
    module: "University • Dissertation",
  },
  {
    id: "suffolk-archives-ner",
    title: "Suffolk Archives NER Pipeline",
    description:
      "A Named Entity Recognition (NER) pipeline built for Suffolk Archives to identify historical references in digitised maps and documents. It enriches metadata, supports geospatial search, and includes a React interface for uploading excerpts, viewing annotated entities, and exploring locations on an interactive Leaflet map.",
    href: "https://github.com/amylouisejordan/suffolk-archives-pipeline",
    icon: <Icon icon={faTimeline} aria-label="Timeline icon" />,
    tags: ["Python", "spaCy", "React", "Leaflet", "NER"],
    module: "University • Information Engineering",
  },
  {
    id: "emergency-alert-system",
    title: "Emergency Alert System",
    description:
      "A lightweight emergency alert system built with MQTT for my Distributed Systems module. It includes a publisher that sends alerts and a subscriber that receives and displays them, demonstrating simple, efficient communication suitable for sensors, mobile devices, and IoT-style messaging.",
    href: "https://github.com/amylouisejordan/emergency-alert-system",
    icon: <Icon icon={faTriangleExclamation} aria-label="Alert icon" />,
    tags: ["MQTT", "Node.js", "Publisher/Subscriber", "Python"],
    module: "University • Distributed Systems",
  },
  {
    id: "password-strength-visualiser",
    title: "Password Strength Visualiser",
    description:
      "A React app that analyses password strength in real time using Dropbox’s zxcvbn library. It adds extra checks like keyboard‑pattern detection and displays a friendly visual strength bar, helping users understand how secure their passwords are as they type.",
    href: "https://github.com/amylouisejordan/password-strength-visualiser",
    icon: <Icon icon={faLock} aria-label="Lock icon" />,
    tags: ["JavaScript", "HTML", "CSS"],
    module: "Personal Project",
  },
];

const Section = styled.section`
  margin-bottom: 48px;
`;

const Title = styled.h2`
  font-size: 25px;
  font-weight: 600;
  color: ${COLOURS.textStrong};
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const Card = styled(motion.a)`
  display: block;
  padding: 16px;
  border: 1px solid ${COLOURS.textMuted};
  border-radius: 10px;
  text-decoration: none;
  color: ${COLOURS.text};
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(3px);
  will-change: background-color, border-color, color;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

  transition: border-color 0.15s linear, background-color 0.15s linear,
    color 0.15s linear;

  &:hover,
  &:focus-visible {
    border-color: ${COLOURS.accent};
    background-color: rgba(82, 39, 255, 0.15);
    color: ${COLOURS.textStrong};
    outline: none;
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${COLOURS.textStrong};
`;

const CardDescription = styled.p`
  font-size: 17px;
  line-height: 1.5;
  margin: 0 0 12px;
  color: ${COLOURS.text};

  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  padding-bottom: 12px;
  margin-bottom: 12px;
`;

const Tags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const Tag = styled.li`
  font-size: 15px;
  padding: 4px 8px;
  border: 1px solid ${COLOURS.textMuted};
  border-radius: 6px;
  color: ${COLOURS.textStrong};
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(3px);
`;

const Module = styled.div`
  font-size: 15px;
  color: ${COLOURS.textMuted};
  margin-top: 8px;
`;

const cardVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const Projects = () => (
  <Section id="projects">
    <Title>Recent Projects</Title>

    <Grid>
      {projects.map((p) => {
        const titleId = `${p.id}-title`;
        const descId = `${p.id}-desc`;

        return (
          <Card
            key={p.id}
            as="a"
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-labelledby={titleId}
            aria-describedby={descId}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={cardVariants}
            transition={{ duration: 0.25 }}
          >
            <CardHeader>
              {p.icon}
              <CardTitle id={titleId}>{p.title}</CardTitle>
            </CardHeader>

            <Module>{p.module}</Module>

            <CardDescription id={descId}>{p.description}</CardDescription>

            <Tags>
              {p.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Tags>
          </Card>
        );
      })}
    </Grid>
  </Section>
);

export default Projects;
